import React, { useEffect, useRef } from 'react';

import { StudentGMetrixTableDialog, StudentGMetrixTableDialogHandle } from 'components/StudentGMetrixTableDialog/StudentGMetrixTableDialog';
import GMetrixDialogConfirm from 'components/GMetrixDialogConfirm/GMetrixDialogConfirm';
import { NewStudentForm } from 'components/NewStudentForm/NewStudentForm';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { useGroupDetails, useGroups } from 'pages/groups/context';
import { useRegisterHeaderControls } from 'components/layout';
import { filterDataBySearchTerm, GenerateUUID } from 'utils';
import { StudentGroupApi } from 'apis/StudentGroupApi';
import { TransactionApi } from 'apis/TransactionApi';
import { useNotification } from 'app/notifications';
import { useNavigate } from 'react-router-dom';
import { Student } from 'apis/StudentApi';
import { GMetrixTable } from 'components';
import { useJoyride } from 'app/joyride';
import { Modal } from 'react-bootstrap';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

import 'pages/groups/components/StudentsTab/styles/students-tab.scss';

interface StudentsTabProps {
    contentTiedToInactiveOrder: boolean;
    groupId: number;
    mode: 'Edit' | 'View';
    students?: Student[];
}

export function StudentsTab({ contentTiedToInactiveOrder, groupId, students }: StudentsTabProps) {

    const [state, setState] = useSuperState({
        loading: false,
        searchTerm: '',
        showModal: false
    });

    const { userPartners } = useApp();
    const { pageContext, updateById } = useGroupDetails();
    const { permissions, studentPermissions } = useGroups();
    const { formatMessage } = useIntl();
    const { catchWithToast, createToast } = useNotification();
    const { loadCurrentTour } = useJoyride();

    const navigate = useNavigate();

    const gmetrixConfirmRef = useRef<any>(null);
    const addStudentsDialogRef = useRef<StudentGMetrixTableDialogHandle>(null);
    const prevShowAddDialogRef = useRef(false);

    // Compute from tab data instead of making API calls
    const countries = pageContext?.Data.countries ?? [];

    const leastRedemptions = (() => {

        let min = 999;
        const transactions = pageContext?.Data.transactions;
        if (transactions && transactions.length > 0) {

            transactions.forEach(t => {

                if (t.UsesRemaining && t.UsesRemaining < min) {

                    min = t.UsesRemaining;

                }

            });

        }
        return min;

    })();

    const hasTransaction = !!(pageContext?.Data.licenses && pageContext.Data.licenses.length > 0);
    const studentsLength = students?.length ?? 0;

    useEffect(() => {

        loadCurrentTour('StudentTab');

    }, []);

    const getAllStudentData = async () => {

        setState({ loading: true });
        try {

            const partnerId = pageContext?.Data.groupDetails?.Partner?.Id ?? null;
            const freshStudents = await StudentGroupApi.GetAllStudents(groupId, partnerId);
            updateById(groupId, { studentList: freshStudents });

        } catch (e) {

            console.error(e);

        } finally {

            setState({ loading: false });

        }

    };

    const deleteGroupItem = async (dataItem: Student | undefined) => {

        if (dataItem && students) {

            setState({ loading: true });
            await StudentGroupApi.DeleteStudentFromGroup(groupId, [dataItem.Id]);
            getAllStudentData();

        }

    };

    const canAddStudent = (maxStudents: number | undefined, action: any) => {

        if (
            (maxStudents && maxStudents > studentsLength) ||
            maxStudents === 0 ||
            maxStudents === undefined ||
            maxStudents === -1
        ) {

            if (
                students !== undefined &&
                pageContext?.Data.groupDetails?.MaxStudents &&
                pageContext.Data.groupDetails.MaxStudents <=
                students?.length
            ) {

                //add error
                createToast(formatMessage({ id: 'students_may_no_longer_be_added_to_this.2eec399f', defaultMessage: 'Students may no longer be added to this Classroom. Please increase the Max Student limit to add more' }), 'danger');

                return;

            }
            action();

        } else {

            createToast(formatMessage({ id: 'you_cannot_add_students_max_students_is.26a58ce1', defaultMessage: 'You cannot add students, Max Students is currently set to {max}' }, { max: maxStudents }), 'danger');

        }

    };

    const onDetailsItemClick = async (dataItem: Student) => {

        navigate(`/students/${dataItem.Id}`);

    };

    // Dial the "Add Students" CTA out to the group detail page header.
    useRegisterHeaderControls(pageContext?.Data.groupDetails ? {
        cta: {
            actions: [
                {
                    label: formatMessage({ id: 'add_existing_students.c9b8b5e7', defaultMessage: 'Add Existing Student(s)' }),
                    onClick: () => canAddStudent(pageContext.Data.groupDetails?.MaxStudents, async () => {

                        await addStudentsDialogRef.current?.show({
                            groupInfo: { leastRedemptions, studentLength: studentsLength },
                            removeTheseStudents: students?.map(student => student.Id),
                            studentAssociation: { associationIds: [pageContext.Id], associationName: 'Group' }
                        });
                        getAllStudentData();

                    })
                },
                {
                    label: formatMessage({ id: 'import_students.f041ccf5', defaultMessage: 'Import Students' }),
                    onClick: () => canAddStudent(pageContext.Data.groupDetails?.MaxStudents, () => window.location.replace(
                        `/students/import?context=group&groupId=${pageContext.Data.groupDetails?.Id}&code=${encodeURIComponent(pageContext.Data.groupDetails?.Control || '')}`))
                },
                {
                    label: formatMessage({ id: 'new_student.411d2e15', defaultMessage: 'New Student' }),
                    onClick: () => canAddStudent(pageContext.Data.groupDetails?.MaxStudents, () => setState({ showModal: true }))
                }
            ],
            disabled:
                !permissions.Edit ||
                !!(pageContext.Data.groupDetails.MaxStudents && pageContext.Data.groupDetails.MaxStudents <= studentsLength) ||
                (contentTiedToInactiveOrder && hasTransaction),
            label: (
                <div className="d-inline-flex align-items-center justify-content-center flex-fill gap-1">
                    <i className="fa-solid fa-plus" />
                    <span>
                        <FormattedMessage defaultMessage="New Student" id="new_student.411d2e15" />
                    </span>
                </div>
            )
        }
    } : null);

    return (
        <>
            {pageContext && pageContext.Data.groupDetails && (
                <>
                    <GMetrixTable
                        allowExport
                        columnData={[
                            {
                                cell: ({ dataItem }) => (
                                    <td>
                                        {dataItem.Firstname}
                                        {' '}
                                        {dataItem.Lastname}
                                    </td>
                                ),
                                field: 'Firstname',
                                title: formatMessage({ id: 'name.5458f379', defaultMessage: 'Name' })
                            },
                            { field: 'Username', title: formatMessage({ id: 'username.dceab582', defaultMessage: 'Username' }) },
                            { field: 'Email', title: formatMessage({ id: 'email.5cbb5845', defaultMessage: 'Email', description: 'Formulario' }) },
                            {
                                cell: ({ dataItem }) => (
                                    <td>
                                        {dataItem.Archived ? (
                                            <i className="fa-solid fa-check text-success fa-lg"></i>
                                        ) : (
                                            <i className="fa-solid fa-dash fa-lg"></i>
                                        )}
                                    </td>
                                ),
                                field: 'Archived',
                                title: formatMessage({ id: 'status.ad856ad7', defaultMessage: 'Status' })
                            },
                            {
                                cell: ({ dataItem }) => (
                                    <td>
                                        {dataItem.LastAccessDate
                                            ? <FormattedDate value={new Date(dataItem.LastAccessDate)} />
                                            : ''}
                                    </td>
                                ),
                                field: 'LastAccessDate',
                                title: formatMessage({ id: 'last_login.0b7d4377', defaultMessage: 'Last Login' })
                            },
                            {
                                cell: ({ dataItem }) => (
                                    <td>
                                        <div className="d-flex justify-content-end mt-1 mb-1">
                                            <GMetrixButton
                                                iconClass="fa-solid fa-arrow-up-right-from-square"
                                                onClick={() => onDetailsItemClick(dataItem)}
                                                variant="outline-primary"
                                            >
                                                <FormattedMessage defaultMessage="Details" id="details.7799b923" />
                                            </GMetrixButton>

                                            <GMetrixButton
                                                onClick={async () => {

                                                    const result = await gmetrixConfirmRef.current.confirm(formatMessage({ id: 'remove_student_from_classroom.647e1266', defaultMessage: 'Remove Student from Classroom' }), formatMessage({ id: 'are_you_sure_you_want_to_remove_this_stu.5f0e6bd6', defaultMessage: 'Are you sure you want to remove this Student from this Classroom?' }));
                                                    if (result) {

                                                        deleteGroupItem(dataItem);

                                                    }

                                                }}
                                                className="ms-2"
                                                iconClass="fa-regular fa-trash"
                                                variant="outline-danger"
                                            ></GMetrixButton>
                                        </div>
                                    </td>
                                ),
                                width: 210
                            }
                        ]}
                        bottomBarTableName={formatMessage({ id: 'students.c6d199ef', defaultMessage: 'Students', description: 'menu' })}
                        key={GenerateUUID().substring(0, 8)}
                        loading={state.loading}
                        rowData={students ? filterDataBySearchTerm(state.searchTerm, students) : []}
                        searchPlaceholder={formatMessage({ id: 'search_students.7398acc2', defaultMessage: 'Search students...' })}
                        searchTerm={state.searchTerm}
                        setSearchTerm={val => setState({ searchTerm: val })}
                    />

                    <GMetrixDialogConfirm ref={gmetrixConfirmRef} />

                    <StudentGMetrixTableDialog
                        multiSelect
                        actionButtonText={formatMessage({ id: 'add_students.c487cc74', defaultMessage: 'Add Student(s)' })}
                        option="Add"
                        partnerId={pageContext?.Data.groupDetails?.Partner?.Id}
                        permissions={permissions}
                        ref={addStudentsDialogRef}
                    />
                    <Modal onHide={() => setState({ showModal: false })} show={state.showModal} size="xl">
                        <Modal.Header closeButton>
                            <Modal.Title>
                                <FormattedMessage defaultMessage="Create Student" id="create_student.b6877d5c" />
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <NewStudentForm
                                onSuccess={async student => {

                                    await StudentGroupApi.AddStudentToGroup([pageContext.Id], [student?.Id]).catch(catchWithToast(formatMessage({ id: 'failed_to_add_student_to_classroom.976fd868', defaultMessage: 'Failed to add student to classroom.' })));

                                    if (pageContext.Data.transactionDetails?.Id) {

                                        await TransactionApi.AddStudentToTransaction([pageContext.Data.transactionDetails?.Id], [student?.Id], pageContext.Data.groupDetails?.Partner?.Id).catch(catchWithToast(formatMessage({ id: 'failed_to_add_student_to_access_code.d2f08719', defaultMessage: 'Failed to add student to access code.' })));

                                    }
                                    getAllStudentData();
                                    setState({ showModal: false });

                                }}
                                ActionsWrapper={Modal.Footer}
                                ContentWrapper={Modal.Body}
                                countries={countries} onClose={() => setState({ showModal: false })}
                                partnerId={pageContext?.Data.groupDetails?.Partner?.Id}
                                permissions={studentPermissions}
                                userPartners={userPartners}
                            />
                        </Modal.Body>

                    </Modal>

                </>
            )}
        </>
    );

}
